/**
 * FAQ Page
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ChevronDown, Search, ArrowRight, HelpCircle } from "lucide-react";

const faqCategories = [
  { id: "general", label: "General Questions" },
  { id: "eligibility", label: "Eligibility & Requirements" },
  { id: "process", label: "Application Process" },
  { id: "products", label: "Funding Products" },
  { id: "costs", label: "Rates & Fees" },
];

const allFaqs = {
  general: [
    { q: "What is Legacy Triangle?", a: "Legacy Triangle is a business funding consultancy that connects entrepreneurs with the right financing solutions. We work with over 100 lenders to find the best options for your business." },
    { q: "How is Legacy Triangle different from a bank?", a: "Unlike banks, we work with multiple lenders to find the best fit for your business. We also offer faster approvals, more flexible requirements, and personalized guidance throughout the process." },
    { q: "Do you work with startups?", a: "Yes! We have funding options for businesses at every stage, including startups. Our Business Credit Cards and Working Capital products are popular choices for newer businesses." },
  ],
  eligibility: [
    { q: "What credit score do I need to qualify?", a: "Credit score requirements vary by product. Most options require 600+, but our Business Credit Builder accepts all credit profiles. We'll match you with the best option for your situation." },
    { q: "How long does my business need to be operating?", a: "Time in business requirements vary. Some products like Working Capital require only 6 months, while SBA loans typically need 2+ years. We have options for businesses at every stage." },
    { q: "Do I need collateral?", a: "Most of our funding options don't require collateral. Equipment financing uses the equipment as collateral, and SBA loans may require some collateral for larger amounts." },
  ],
  process: [
    { q: "How long does the application process take?", a: "Our initial application takes just 5-10 minutes. Approval times vary: Business Credit Cards (24-48 hours), Working Capital (1-3 days), Equipment Financing (3-7 days), SBA Loans (30-90 days)." },
    { q: "What documents do I need?", a: "Basic requirements include: 3-6 months bank statements, recent tax returns, valid ID, and business information. SBA loans require additional documentation. We'll provide a complete checklist." },
    { q: "Will applying affect my credit score?", a: "Our initial prequalification uses a soft credit pull (no impact). Hard pulls only occur when you proceed with a full application to a specific lender." },
  ],
  products: [
    { q: "What types of funding do you offer?", a: "We offer Business Credit Cards, SBA Loans, Equipment Financing, Business Lines of Credit, Working Capital, and our Business Credit Builder program." },
    { q: "What are Business Credit Cards?", a: "Business Credit Cards strategically combine multiple business credit cards to access $5K-$100K+ in working capital quickly, often with 0% intro APR periods." },
    { q: "What's the difference between a line of credit and a term loan?", a: "A line of credit is revolving—you draw funds as needed and only pay interest on what you use. Term loans provide a lump sum with fixed monthly payments over a set period." },
  ],
  costs: [
    { q: "Are there upfront fees?", a: "No upfront fees. We operate on a success-based model—we only earn a fee when you successfully receive funding." },
    { q: "What interest rates can I expect?", a: "Rates vary by product: SBA Loans (6.5-10.5%), Equipment (5-15%), Lines of Credit (7-18%), Working Capital (8-35%). Your actual rate depends on your business profile." },
    { q: "Are there prepayment penalties?", a: "Most of our funding options have no prepayment penalties, allowing you to pay off early and save on interest. Some SBA loans may have prepayment restrictions for the first 3 years." },
  ],
};

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState("general");
  const [openFaq, setOpenFaq] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqs = allFaqs[activeCategory] || [];

  const filteredFaqs = searchTerm
    ? Object.values(allFaqs)
        .flat()
        .filter((faq) =>
          faq.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
          faq.a.toLowerCase().includes(searchTerm.toLowerCase())
        )
    : faqs;

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      <Navbar />

      {/* Hero */}
      <section className="bg-[#0f2d52] pt-32 pb-16">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-8 bg-[#48d480]" />
              <span className="section-label">FAQ</span>
              <div className="h-px w-8 bg-[#48d480]" />
            </div>
            <h1 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-4xl md:text-5xl font-black text-white mb-4">
              Frequently Asked <span className="text-[#48d480]">Questions</span>
            </h1>
            <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/70 mb-8">
              Find answers to common questions about our funding process and services.
            </p>

            {/* Search */}
            <div className="relative max-w-lg mx-auto">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search for answers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-xl bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#48d480]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories + FAQs */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar Categories */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl p-6 border border-gray-200 sticky top-32">
                <h3 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-[#0f2d52] font-bold text-sm mb-4">Categories</h3>
                <div className="space-y-2">
                  {faqCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => { setActiveCategory(cat.id); setSearchTerm(""); setOpenFaq(null); }}
                      className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                        activeCategory === cat.id && !searchTerm
                          ? "bg-[#48d480] text-[#0f2d52]"
                          : "text-gray-600 hover:bg-gray-100"
                      }`}
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* FAQ List */}
            <div className="lg:col-span-3">
              <div className="space-y-3">
                {filteredFaqs.length > 0 ? (
                  filteredFaqs.map((faq, idx) => (
                    <div
                      key={idx}
                      className={`bg-white rounded-xl border transition-all ${
                        openFaq === idx ? "border-[#48d480] shadow-md" : "border-gray-200"
                      }`}
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                        className="w-full flex items-center justify-between p-5 text-left"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${openFaq === idx ? "bg-[#48d480]" : "bg-gray-100"}`}>
                            <HelpCircle size={16} className={openFaq === idx ? "text-[#0f2d52]" : "text-gray-500"} />
                          </div>
                          <span style={{ fontFamily: "'Inter', sans-serif" }} className="text-[#0f2d52] font-semibold text-sm">{faq.q}</span>
                        </div>
                        <ChevronDown size={18} className={`text-[#48d480] transition-transform ${openFaq === idx ? "rotate-180" : ""}`} />
                      </button>
                      {openFaq === idx && (
                        <div className="px-5 pb-5">
                          <div className="border-t border-gray-100 pt-4 pl-11">
                            <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                    <HelpCircle size={40} className="text-gray-300 mx-auto mb-4" />
                    <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-gray-500">No matching questions found.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0f2d52]">
        <div className="container text-center max-w-2xl mx-auto">
          <h2 style={{ fontFamily: "'Montserrat', sans-serif" }} className="text-3xl md:text-4xl font-black text-white mb-4">
            Still Have <span className="text-[#48d480]">Questions?</span>
          </h2>
          <p style={{ fontFamily: "'Inter', sans-serif" }} className="text-white/60 mb-8">
            Our funding specialists are here to help. Schedule a free consultation.
          </p>
          <Link to="/contact">
            <button className="btn-primary flex items-center gap-2 mx-auto">
              Contact Us <ArrowRight size={16} />
            </button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
